import type { ServiceDict } from './externals'
import { resolveLoginHost } from './externals'
import { AppService } from './services/AppService'
import { LoginService } from './services/LoginService'
import { PollService } from './services/PollService'
import { StripeService } from './services/StripeService'
import { ThumbService } from './services/ThumbService'
import { VideoService } from './services/VideoService'

/**
 * SessionServiceProvider manages service instances for the current session.
 *
 * This replaces the old HostProvider pattern by:
 * 1. Using session info from middleware (authToken + serviceDict) instead of reading from storage
 * 2. Creating service singletons per session - no need for separate "logged in" vs "per job" methods
 * 3. Services are lazily instantiated and cached for the session lifetime
 *
 * The provider is initialized by sessionMiddleware before loaders run,
 * ensuring services are available throughout the request lifecycle.
 *
 * Service Constructor Patterns:
 * - Optional auth services (AppService, ThumbService, VideoService):
 *   constructor(services: ServiceDict, authToken: string | undefined)
 *   These can work with or without auth for public job access.
 * - Required auth services (StripeService):
 *   constructor(services: ServiceDict, authToken: string)
 * - Unauthenticated services (PollService):
 *   constructor(services: ServiceDict)
 * - Pre-auth services (LoginService):
 *   constructor(hostname: string) - uses resolveLoginHost()
 */
class SessionServiceProvider {
    private authToken: string | undefined
    private serviceDict: ServiceDict | undefined
    // Cached service instances
    private appService: AppService | undefined
    private thumbService: ThumbService | undefined
    private videoService: VideoService | undefined
    private pollService: PollService | undefined
    private stripeService: StripeService | undefined
    private loginService: LoginService | undefined

    /**
     * Initialize the provider with session configuration.
     * Called from sessionMiddleware before loaders run.
     */
    public initialize(authToken: string | undefined, serviceDict: ServiceDict | undefined): void {
        // Clear cached services if auth token changes
        // Only authToken matters for cache invalidation - if token changes, user session changed
        if (this.authToken !== authToken) {
            this.clearCache()
        }

        this.authToken = authToken
        this.serviceDict = serviceDict
    }

    private clearCache(): void {
        this.appService = undefined
        this.thumbService = undefined
        this.videoService = undefined
        this.pollService = undefined
        this.stripeService = undefined
        // Note: loginService is not cleared as it doesn't depend on session
    }

    private ensureInitialized(): void {
        if (this.serviceDict === undefined) {
            throw new Error(
                'SessionServiceProvider not initialized. Ensure sessionMiddleware runs before accessing services.',
            )
        }
    }

    private ensureAuthenticated(): void {
        this.ensureInitialized()
        if (this.authToken === undefined) {
            throw new Error(
                'Session not authenticated. Ensure user is logged in before accessing authenticated services.',
            )
        }
    }

    /**
     * Get AppService instance for the current session.
     * Creates new instance if not cached.
     * Note: AppService can work with or without authentication.
     * Unauthenticated access is allowed for public job endpoints.
     */
    public getAppService(): AppService {
        this.ensureInitialized()

        if (!this.appService) {
            this.appService = new AppService(this.serviceDict!, this.authToken)
        }
        return this.appService
    }

    /**
     * Get ThumbService instance for the current session.
     * Creates new instance if not cached.
     * Note: ThumbService can work with or without authentication
     * for public job thumbnail access.
     */
    public getThumbService(): ThumbService {
        this.ensureInitialized()

        if (!this.thumbService) {
            this.thumbService = new ThumbService(this.serviceDict!, this.authToken)
        }
        return this.thumbService
    }

    /**
     * Get VideoService instance for the current session.
     * Creates new instance if not cached.
     * Note: VideoService can work with or without authentication
     * for public job video access.
     */
    public getVideoService(): VideoService {
        this.ensureInitialized()

        if (!this.videoService) {
            this.videoService = new VideoService(this.serviceDict!, this.authToken)
        }
        return this.videoService
    }

    /**
     * Get PollService instance for the current session.
     * Creates new instance if not cached.
     * Note: PollService doesn't require authentication.
     */
    public getPollService(): PollService {
        this.ensureInitialized()

        if (!this.pollService) {
            this.pollService = new PollService(this.serviceDict!)
        }
        return this.pollService
    }

    /**
     * Get StripeService instance for the current session.
     * Creates new instance if not cached.
     */
    public getStripeService(): StripeService {
        this.ensureAuthenticated()

        if (!this.stripeService) {
            this.stripeService = new StripeService(this.serviceDict!, this.authToken!)
        }
        return this.stripeService
    }

    /**
     * Get LoginService instance.
     * This service is available without session initialization (pre-auth).
     * Creates new instance if not cached.
     */
    public getLoginService(): LoginService {
        if (!this.loginService) {
            this.loginService = new LoginService(resolveLoginHost())
        }
        return this.loginService
    }
}

// Singleton instance
let instance: SessionServiceProvider | undefined

/**
 * Get the SessionServiceProvider singleton.
 * Creates instance if not exists.
 */
export function getSessionServiceProvider(): SessionServiceProvider {
    if (!instance) {
        instance = new SessionServiceProvider()
    }
    return instance
}

/**
 * Initialize SessionServiceProvider with session configuration.
 * Should be called from sessionMiddleware.
 */
export function initializeSessionServiceProvider(
    authToken: string | undefined,
    serviceDict: ServiceDict | undefined,
): void {
    getSessionServiceProvider().initialize(authToken, serviceDict)
}

/**
 * Reset the SessionServiceProvider instance.
 * Primarily for testing purposes.
 */
export function resetSessionServiceProvider(): void {
    instance = undefined
}

// Convenience functions for getting services
export const getAppService = (): AppService => getSessionServiceProvider().getAppService()
export const getThumbService = (): ThumbService => getSessionServiceProvider().getThumbService()
export const getVideoService = (): VideoService => getSessionServiceProvider().getVideoService()
export const getPollService = (): PollService => getSessionServiceProvider().getPollService()
export const getStripeService = (): StripeService => getSessionServiceProvider().getStripeService()
export const getLoginService = (): LoginService => getSessionServiceProvider().getLoginService()
